import { useMemo } from 'react';
import { useFilteredQuery } from './useFilteredQuery';
import { cleanObjectFromUndefined } from '../helpers';

const queryTypes = {
  independent: 'independent',
  dependent: 'dependent',
};

export const useAdditionalQueries = ({
  additionalQueries = {},
  dependentAdditionalQueries = {},
  commonVars = {},
  options = {},
  data = [],
  getDataForDependentQueryMethods = {},
}) => {
  const dependentQueryVariables = useMemo(() => {
    if (data.length && Object.keys(getDataForDependentQueryMethods).length) {
      return Object.entries(getDataForDependentQueryMethods).reduce(
        (acc, [queryKey, queryMethod]) => ({
          ...acc,
          [queryKey]: queryMethod(data),
        }),
        {},
      );
    }
    return {};
  }, [data, getDataForDependentQueryMethods]);

  const allQueries = useMemo(() => {
    const queries = [];

    Object.entries(additionalQueries).forEach(([queryKey, queryConfig]) => {
      queries.push({
        key: queryKey,
        type: queryTypes.independent,
        config: queryConfig,
      });
    });

    Object.entries(dependentAdditionalQueries).forEach(([queryKey, queryConfig]) => {
      queries.push({
        key: queryKey,
        type: queryTypes.dependent,
        config: queryConfig,
      });
    });

    return queries;
  }, [additionalQueries, dependentAdditionalQueries]);

  const queryResults = allQueries.map(({ key, type, config }) => {
    const isDependent = type === queryTypes.dependent;
    const skipCondition = isDependent
      ? config.skip || !Object.keys(dependentQueryVariables?.[key] || {}).length
      : config.skip;

    const variables = cleanObjectFromUndefined({
      ...(isDependent ? dependentQueryVariables[key] : commonVars),
      ...config.queryCustomVariables,
      ...options,
    });

    const result = useFilteredQuery(config.query, {
      variables,
      skip: skipCondition,
      notifyOnNetworkStatusChange: config.notifyOnNetworkStatusChange ?? true,
      fetchPolicy: config.fetchPolicy,
      ...config.queryOptions,
    });

    return {
      key,
      result,
      skip: skipCondition,
    };
  });

  const additionalQueriesResults = useMemo(() => queryResults.reduce((
    acc,
    { key, result, skip },
  ) => {
    const queryData = result.usableData || result.data;
    return {
      ...acc,
      [key]: {
        loading: result.loading,
        error: result.error,
        data: queryData,
        refetch: skip ? () => {} : result.refetch,
      },
    };
  }, {}), [queryResults]);

  return additionalQueriesResults;
};

export default useAdditionalQueries;
